import type { Launch, LaunchMedia } from "../types/launch";

export const OTHER_HFB = "OTHER";

export type LaunchMediaRow = { launch: Launch; media: LaunchMedia };
export type LaunchMediaSectionData = { launch: Launch; medias: LaunchMedia[] };
export type MediasByLaunchId = Record<string, LaunchMedia[] | undefined>;

export const getMediaHfb = (media: LaunchMedia): string => {
  const value = media.attributes?.find((a) => a.attribute === "hfb")?.value;
  const trimmed = value?.trim();
  return trimmed && /^[0-9]{2}$/.test(trimmed) ? trimmed : OTHER_HFB;
};

export const toFiscalYearLabel = (year: number): string =>
  `FY${String(year).slice(-2)}`;

export const flattenRows = (
  launches: Launch[],
  mediasByLaunchId: MediasByLaunchId
): LaunchMediaRow[] =>
  launches.flatMap((launch) =>
    (mediasByLaunchId[launch.id] ?? []).map((media) => ({ launch, media }))
  );

/** Groups rows by launch in the original launch order, dropping empty groups. */
export const groupRowsByLaunch = (
  launches: Launch[],
  rows: LaunchMediaRow[]
): LaunchMediaSectionData[] =>
  launches
    .map((launch) => ({
      launch,
      medias: rows.filter((r) => r.launch.id === launch.id).map((r) => r.media),
    }))
    .filter(({ medias }) => medias.length > 0);

export const sortedUnique = <T extends string | number>(values: T[]): T[] =>
  Array.from(new Set(values)).sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));

/**
 * Matches when nothing is selected, or when the selection (restricted to the
 * currently available options) is empty or contains the row's value.
 */
export const matchesSelection = (
  selected: unknown,
  options: string[],
  rowValue: string
): boolean => {
  if (!Array.isArray(selected)) return true;
  const valid = (selected as string[]).filter((v) => options.includes(v));
  return valid.length === 0 || valid.includes(rowValue);
};
